import React from 'react';
import { z } from 'zod';
import { CandidatesConst, StateCodesConst } from '@axiom/const';
import {
  Button,
  Dropdown,
  Form,
  Gutter,
  Input,
  Modal,
  ModalHeader,
  ModalSection,
  ModalFooter,
  Paragraph,
  SmallHeader,
  ToastUtil,
  CondensedHeader,
  CondensedLarge,
} from '@axiom/ui';
import { MlRecommendedUtil } from '@axiom/utils';
import type { Opportunity } from '@axiom/validation';
import { CandidateProfileStatuses } from '@axiom/validation';
import { ZodArrayToEnum } from '@axiom/types';

import { CandidateUtil } from '../../utils/candidate-util';
import { MlRecommendationsApi } from '../../api/ml-recommendations-api';

const { ProfileStatusLists } = CandidatesConst;

const profileStatusOptions = CandidateUtil.profileFilterStatusOptions.filter(
  ({ label }) => !['Alum DNR', 'Pending Alum DNR', 'Rejected'].includes(label)
);

const formSchema = z.object({
  addressState: z.array(z.string()).nullish(),
  annualCompensationEnd: z
    .number()
    .nonnegative()
    .max(9999999999999.99)
    .nullish(),
  profileStatus: z
    .array(z.enum(ZodArrayToEnum(CandidateProfileStatuses)))
    .min(1)
    .nullish(),
  size: z.number().int().gte(1).lte(10).nullish(),
  weeklyAvailability: z.number().nonnegative().nullish(),
});
export type FormSchema = z.infer<typeof formSchema>;

export const formatFilterStructure = ({
  size,
  addressState,
  weeklyAvailability,
  annualCompensationEnd,
  profileStatus,
}: FormSchema) => {
  return {
    ...(addressState?.length && { addressState }),
    annualCompensationStart: 0,
    ...(annualCompensationEnd && {
      annualCompensationEnd,
    }),
    ...(profileStatus && { profileStatus }),
    ...(size && { size }),
    ...(weeklyAvailability && { weeklyAvailability }),
  };
};

export type GetMoreMatchesModalProps = {
  opportunity: Opportunity;
  onRequestSuccess: ({ filters }: { filters: FormSchema }) => void;
  onClose: () => void;
  savedFilters: FormSchema;
};

export const GetMoreMatchesModal = ({
  opportunity,
  onRequestSuccess,
  onClose,
  savedFilters,
}: GetMoreMatchesModalProps) => {
  const initialValues: FormSchema = {
    weeklyAvailability:
      (opportunity.requiredBillingHoursPerWeek ?? null) === null
        ? 0
        : Math.floor((opportunity.requiredBillingHoursPerWeek ?? 0) / 5) * 5,
    addressState: [],
    profileStatus: [...ProfileStatusLists.allCertifiedTalent.value],
    size: 3,
    ...savedFilters,
  };

  return (
    <Form
      name="GET_MORE_MATCHES_MODAL_FORM"
      schema={formSchema}
      initialValues={initialValues}
      onSubmit={async formData => {
        const completeFormData = { ...initialValues, ...formData };
        const translatedFilters = formatFilterStructure(completeFormData);

        const { data: recommendationCount } =
          await MlRecommendationsApi.createRecommendations({
            filters: {
              addressCountryCode: opportunity.requiredTalentCountryCode,
              businessTitles: MlRecommendedUtil.getDesiredBusinessTitles(
                opportunity.positions
              ),
              ...translatedFilters,
            },
            opportunityId: opportunity.id,
          });
        if (recommendationCount === 0) {
          ToastUtil.add({
            dismissible: true,
            name: `NOCANDFOUND`,
            type: 'error',
            children: (
              <>
                <CondensedHeader>Error</CondensedHeader>
                <CondensedLarge>
                  We couldn't find any talent that matches your criteria. Let's
                  broaden your search and try again.
                </CondensedLarge>
              </>
            ),
          });
        } else {
          await MlRecommendationsApi.refreshRecommendations(opportunity.id);
          onClose();
        }
        onRequestSuccess({ filters: completeFormData });
      }}
    >
      {({ fireSubmit, values }) => {
        const filters = JSON.stringify(formatFilterStructure(values));

        return (
          <Modal name="GET_MORE_MATCHES_MODAL">
            <ModalHeader onClose={onClose}>Refine Matches</ModalHeader>
            <ModalSection>
              <div
                data-test="pendo-analytics-get-more-matches-modal"
                data-pendo-analytics-id="get-more-matches-click"
                data-pendo-analytics-opportunity-id={opportunity.id}
                data-pendo-analytics-get-more-matches-filters={filters}
              >
                <SmallHeader name="jobName">{opportunity.jobName}</SmallHeader>
                <Gutter bottom="16px" />
                <Paragraph>
                  Edit the options below to find more AI matches for this
                  opportunity.
                </Paragraph>
                <Gutter bottom="16px" />
                <Dropdown
                  name="size"
                  label="How many matches"
                  options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(i => ({
                    label: `${i}`,
                    value: i,
                  }))}
                  displayKey="label"
                  valueKey="value"
                />
                <Gutter bottom="16px" />
                <Dropdown
                  name="profileStatus"
                  label="Profile Status"
                  options={profileStatusOptions}
                  displayKey="label"
                  valueKey="value"
                />
                <Gutter bottom="16px" />
                <Dropdown
                  name="weeklyAvailability"
                  label="Availability"
                  options={[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50].map(
                    i => ({
                      label: `${i} or more`,
                      value: i,
                    })
                  )}
                  displayKey="label"
                  valueKey="value"
                />
                <Gutter bottom="16px" />
                {opportunity.requiredTalentCountryCode === 'US' ? (
                  <>
                    <Dropdown
                      name="addressState"
                      label="Location"
                      options={Object.values(
                        StateCodesConst.StateCodesWithoutTerritories
                      ).map(name => ({
                        label: name,
                        value: name,
                      }))}
                      displayKey="label"
                      valueKey="value"
                    />
                    <Gutter bottom="16px" />
                  </>
                ) : null}
                <Input
                  name="annualCompensationEnd"
                  label="Maximum Annual Compensation"
                />
              </div>
            </ModalSection>
            <ModalFooter>
              <Button
                name="GET_MORE_MATCHES_MODAL_CANCEL_BUTTON"
                onClick={onClose}
                variation="outline"
              >
                Cancel
              </Button>
              <Button
                name="PENDO_GET_MORE_MATCHES_MODAL_SUBMIT_BUTTON"
                onClick={fireSubmit}
              >
                Submit
              </Button>
            </ModalFooter>
          </Modal>
        );
      }}
    </Form>
  );
};
